import type { ReactNode } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { Fab, styled } from "@mui/material";
import type { FabProps } from "@mui/material";

import { BACK_BUTTON_NAME } from "@/constants/layout";
import { Z_INDEX } from "@/constants/zIndex";

/**
 * The close / back control shared by every screen drawer — the original's
 * module 84919.
 *
 * Three details matter and are easy to get wrong:
 *
 * 1. It is a MUI **`Fab`**, not an `IconButton`, so it measures 30×30 at the
 *    theme's small-button height and carries the theme's own elevation.
 * 2. `title="Close"` is applied even when the child is a ChevronLeft — the
 *    original never varies it.
 * 3. `name={BACK_BUTTON_NAME}` is what wires the browser's back button to this
 *    control; the global `popstate` listener clicks the last one on the page.
 *
 * The click handler stops propagation before delegating, because these Fabs sit
 * inside headers that are themselves clickable rows in some screens.
 */
const StyledFab = styled(Fab)({ zIndex: Z_INDEX.BASE });

interface BackFabProps extends Omit<FabProps, "children"> {
  children?: ReactNode;
}

export default function BackFab({ children, onClick, ...rest }: BackFabProps) {
  return (
    <StyledFab
      color="secondary"
      size="small"
      title="Close"
      {...rest}
      name={BACK_BUTTON_NAME}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        onClick?.(event);
      }}
    >
      {children ?? <CloseIcon fontSize="medium" />}
    </StyledFab>
  );
}
